import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { subscribeToImmunizations, createImmunization, updateImmunization, deleteImmunization } from '../../services/immunizationService';
import { DatePicker } from '@mui/x-date-pickers';

const ImmunizationsSection = ({ patientId }) => {
  const [immunizations, setImmunizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editImmunization, setEditImmunization] = useState(null);
  const [form, setForm] = useState({
    vaccine: '',
    date: '',
    provider: '',
    lotNumber: '',
    notes: ''
  });

  useEffect(() => {
    console.log('🔍 ImmunizationsSection useEffect triggered with dependencies:', {
      patientId: patientId,
      timestamp: new Date().toISOString()
    });

    if (!patientId) {
      console.log('❌ ImmunizationsSection: No patient ID, skipping subscription setup');
      return;
    }

    console.log('📡 ImmunizationsSection: Setting up immunizations subscription for patient:', patientId);
    
    // Subscribe to immunizations using service layer
    const cleanup = subscribeToImmunizations(patientId, (immunizationList) => {
      console.log('💉 ImmunizationsSection: Immunizations update received:', {
        timestamp: new Date().toISOString(),
        immunizationCount: immunizationList.length,
        immunizations: immunizationList.map(i => ({ 
          id: i.id, 
          vaccineName: i.vaccineName?.substring(0, 30) + '...',
          administeredDate: i.administeredDate 
        }))
      });
      
      setImmunizations(immunizationList);
      setLoading(false);
    });

    console.log('✅ ImmunizationsSection: Subscription setup complete, returning cleanup function');
    return () => {
      console.log('🧹 ImmunizationsSection: Cleaning up immunizations subscription');
      cleanup();
    };
  }, [patientId]);

  const handleOpenDialog = (immunization = null) => {
    setEditImmunization(immunization);
    setForm(
      immunization || {
        vaccine: '',
        date: '',
        provider: '',
        lotNumber: '',
        notes: ''
      }
    );
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditImmunization(null);
    setForm({ vaccine: '', date: '', provider: '', lotNumber: '', notes: '' });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (editImmunization) {
        // Update using service layer
        await updateImmunization(editImmunization.id, form);
      } else {
        // Add using service layer
        await createImmunization({
          ...form,
          patientId
        });
      }
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save immunization');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this immunization?')) return;
    try {
      setLoading(true);
      await deleteImmunization(id);
    } catch (err) {
      setError('Failed to delete immunization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mt={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Immunizations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Immunization
        </Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vaccine</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Lot Number</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {immunizations.map((im) => (
                <TableRow key={im.id}>
                  <TableCell>{im.vaccine}</TableCell>
                  <TableCell>{im.date}</TableCell>
                  <TableCell>{im.provider}</TableCell>
                  <TableCell>{im.lotNumber}</TableCell>
                  <TableCell>{im.notes}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleOpenDialog(im)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(im.id)} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {immunizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No immunizations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editImmunization ? 'Edit Immunization' : 'Add Immunization'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Vaccine"
            name="vaccine"
            value={form.vaccine}
            onChange={handleChange}
            fullWidth
            required
          />
          <DatePicker
            label="Date"
            value={form.date ? new Date(form.date) : null}
            onChange={(date) => handleChange({ target: { name: 'date', value: date ? date.toISOString().split('T')[0] : '' } })}
            slotProps={{
              textField: { margin: 'dense', fullWidth: true, required: true }
            }}
          />
          <TextField
            margin="dense"
            label="Provider"
            name="provider"
            value={form.provider}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Lot Number"
            name="lotNumber"
            value={form.lotNumber}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editImmunization ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImmunizationsSection; 